import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Tabs,
  Tab,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import api from '../api';

const WHATS_INCLUDED = {
  BASIC: {
    title: 'Basic',
    includes: ['Identity verification (where available)', 'Baseline record search (where available)'],
    goodFor: 'Quickly filtering applications before showings or final selection',
    limitations: 'Coverage/availability varies by jurisdiction and provider'
  },
  STANDARD: {
    title: 'Standard',
    includes: ['Everything in Basic', 'Eviction/public records (where available)'],
    goodFor: 'Reducing risk on long-term leases',
    limitations: 'Some records are not available in all locations'
  },
  PREMIUM: {
    title: 'Premium',
    includes: ['Everything in Standard', 'Credit and/or income verification (where available and enabled)'],
    goodFor: 'Final decision for higher-rent or higher-risk units',
    limitations: 'Credit/income checks may require additional applicant steps and permissions'
  }
};

function CompareScreeningProviders() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const applicationId = searchParams.get('applicationId') || searchParams.get('applicationID');
  const paidBy = searchParams.get('paidBy') || 'applicant';

  const [config, setConfig] = useState({ countries: ['CA', 'US'], packages: [], providers: [] });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [country, setCountry] = useState('CA');
  const [packageId, setPackageId] = useState('STANDARD');
  const [whatsIncludedOpen, setWhatsIncludedOpen] = useState(false);
  const [whatsIncludedPackage, setWhatsIncludedPackage] = useState('BASIC');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await api.get('/api/billing/comparison-config');
        setConfig(res.data || { countries: [], packages: [], providers: [] });
        if (res.data?.packages?.length && !res.data.packages.find(p => p.id === packageId)) {
          setPackageId(res.data.packages[0].id);
        }
      } catch (err) {
        setSnackbar({ open: true, message: 'Failed to load screening options.', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const providersForCountry = config.providers.filter(p => p.countries && p.countries.includes(country));
  const selectedPackage = config.packages.find(p => p.id === packageId);
  const sku = selectedPackage?.skuByCountry?.[country];

  const handleUseOption = async (providerId) => {
    if (!applicationId || !sku) {
      setSnackbar({ open: true, message: 'Missing application or package. Open from Request Screening.', severity: 'warning' });
      return;
    }
    try {
      setCreating(true);
      const res = await api.post('/api/billing/screeningOrders', {
        applicationId,
        sku,
        paidBy: paidBy === 'landlord' ? 'landlord' : 'applicant',
        providerId
      });
      if (res.data?.inviteUrl) {
        setSnackbar({ open: true, message: 'Screening order created. Share the invite link with the applicant.', severity: 'success' });
        window.location.href = res.data.inviteUrl;
      } else {
        navigate('/');
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to create screening order', severity: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const openWhatsIncluded = (pkgId) => {
    setWhatsIncludedPackage(pkgId);
    setWhatsIncludedOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography>Loading screening options…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Back
      </Button>

      <Typography variant="h5" gutterBottom>
        Compare tenant screening options
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        All options follow consent requirements. Choose the best fit for your country and the checks you need.
      </Typography>

      {!applicationId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Open this page from Request Screening (with an application selected) to create an order. You can still compare options below.
        </Alert>
      )}

      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
        Step 1: Choose country
      </Typography>
      <Tabs value={country} onChange={(_, v) => setCountry(v)} sx={{ mb: 3 }}>
        <Tab label="Canada" value="CA" />
        <Tab label="United States" value="US" />
      </Tabs>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
        Step 2: Choose package
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        {(config.packages || []).map(pkg => (
          <Button
            key={pkg.id}
            variant={packageId === pkg.id ? 'contained' : 'outlined'}
            onClick={() => setPackageId(pkg.id)}
            sx={{ textTransform: 'none' }}
          >
            {pkg.label}
          </Button>
        ))}
      </Box>
      {selectedPackage && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {selectedPackage.description}
        </Typography>
      )}

      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3, mb: 1 }}>
        Step 3: Choose provider
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {providersForCountry.length === 0 ? (
          <Typography color="text.secondary">No providers available for this country.</Typography>
        ) : (
          providersForCountry.map((provider, idx) => (
            <Card key={provider.id} variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {idx === 0 && <Chip label="Recommended" color="primary" size="small" />}
                  <Typography variant="h6">{provider.displayName}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Powered by {provider.displayName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Coverage: {provider.countries?.includes('CA') && provider.countries?.includes('US') ? 'Canada + US' : country === 'CA' ? 'Canada' : 'United States'}
                </Typography>
                {provider.checksByPackage?.[packageId]?.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {provider.checksByPackage[packageId].map(c => (
                      <Chip key={c} label={c.replace(/_/g, ' ')} size="small" variant="outlined" />
                    ))}
                  </Box>
                )}
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Who can pay: Applicant or Landlord
                </Typography>
                {provider.turnaround && (
                  <Typography variant="body2" color="text.secondary">
                    Typical turnaround: {provider.turnaround.minHours}–{provider.turnaround.maxHours} hours
                  </Typography>
                )}
                {(provider.pricing?.mode === 'ZENRENFLOW_PRICE' || provider.pricing?.mode === 'RANGE') && provider.pricing?.minCents != null && (
                  <Typography variant="body2">
                    {provider.pricing.mode === 'ZENRENFLOW_PRICE' && provider.pricing.minCents === (provider.pricing.maxCents ?? provider.pricing.minCents)
                      ? `Estimated: $${(provider.pricing.minCents / 100).toFixed(2)} ${provider.pricing.currency || 'CAD'}`
                      : `Estimated: $${(provider.pricing.minCents / 100).toFixed(2)}–${((provider.pricing.maxCents || provider.pricing.minCents) / 100).toFixed(2)} ${provider.pricing.currency || 'CAD'}`
                    }
                  </Typography>
                )}
                {provider.pricing?.mode === 'VARIES_ONLY' && (
                  <Typography variant="body2">Varies (contract pricing)</Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!applicationId || creating}
                    onClick={() => handleUseOption(provider.id)}
                  >
                    {creating ? 'Creating…' : 'Use this option'}
                  </Button>
                  <Button
                    size="small"
                    startIcon={<InfoOutlinedIcon />}
                    onClick={() => openWhatsIncluded(packageId)}
                  >
                    What&apos;s included?
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))
        )}
      </Box>

      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 3 }}>
        Prices shown are estimates. Final cost may vary by jurisdiction, search type, and provider requirements.
      </Typography>

      <Dialog open={whatsIncludedOpen} onClose={() => setWhatsIncludedOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>What&apos;s included? — {WHATS_INCLUDED[whatsIncludedPackage]?.title || whatsIncludedPackage}</DialogTitle>
        <DialogContent>
          {WHATS_INCLUDED[whatsIncludedPackage] && (
            <>
              <Typography variant="subtitle2" gutterBottom>Includes</Typography>
              <ul style={{ marginTop: 0 }}>
                {WHATS_INCLUDED[whatsIncludedPackage].includes.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
              <Typography variant="subtitle2" gutterBottom>Good for</Typography>
              <Typography variant="body2" paragraph>{WHATS_INCLUDED[whatsIncludedPackage].goodFor}</Typography>
              <Typography variant="subtitle2" gutterBottom>Limitations</Typography>
              <Typography variant="body2">{WHATS_INCLUDED[whatsIncludedPackage].limitations}</Typography>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default CompareScreeningProviders;
